import Box from '@mui/material/Box';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

type MuiFormDatePickerPropsT = {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  maxDate?: Date;
  minDate?: Date;
};

const MuiFormDatePicker = ({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  maxDate,
  minDate,
}: MuiFormDatePickerPropsT): JSX.Element => {
  const { control } = useFormContext();

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="en"
      localeText={{
        fieldMonthPlaceholder: (params: { contentType: string }): string =>
          params.contentType === 'letter' ? 'MMM' : 'MM',
      }}
    >
      <Controller
        name={name}
        control={control}
        rules={{ required: required ? `${label} is required` : false }}
        render={({ field, fieldState }): React.ReactElement => (
          <Box>
            <DatePicker
              {...field}
              label={label}
              value={field.value ? dayjs(field.value) : null}
              onChange={(date: Dayjs | null): void => {
                field.onChange(date ? date.toDate() : null);
              }}
              disabled={disabled}
              maxDate={maxDate ? dayjs(maxDate) : undefined}
              minDate={minDate ? dayjs(minDate) : undefined}
              format="MMM DD, YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  placeholder,
                  required,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message ?? null,
                  sx: {
                    '&.MuiTextField-root': {
                      input: {
                        padding: '8px',
                        fontSize: '13px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '13px',
                      transform: !field.value
                        ? 'translate(14px, 8px) scale(1)'
                        : 'translate(14px, -9px) scale(0.75)',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                    },
                    '& .MuiFormHelperText-root': { fontSize: '11px' },
                  },
                },
              }}
            />
          </Box>
        )}
      />
    </LocalizationProvider>
  );
};

export default MuiFormDatePicker;
